import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from 'file-saver';
import { CreateInvoiceBatchByOrderIdsArgs, InvoiceBatchService, InvoiceTypeEnum, LongRunningOperationModel, LongRunningOperationService, MailTemplateService, OrderActionService, OrderDetailsService, OrderDetailsView, OrderService, PendingOrderService, SendMailETicketArgs, SendPaymentInvitationArgs, TicketDownloadArgs, TicketPrintArgs, TicketService } from "../../../api";
import { ConfirmService, CultureService, DialogHelper, DialogWidth, HttpLoaderService, NotificationCommunicationService, PrintOrDownloadTicketsComponent, SnackbarService } from "@tymes4-shared";
import { ApplicationModuleHelperService } from "../../../services/application-module.service";
import { PrintOrderHardcardsComponent } from "../../../dialogs/print-order-hardcards/print-order-hardcards.component";
import { BulkCreditOrderLinesComponent } from "../../../dialogs/bulk-credit-order-lines/bulk-credit-order-lines.component";
import { ManualMailDialogComponent, ManualMailDialogParameters, ManualMailDialogResult } from "../../../dialogs/manual-mail-dialog/manual-mail-dialog.component";
import { CreatePaymentRequestDialogComponent, CreatePaymentRequestDialogData, CreatePaymentRequestDialogResult } from "../../../dialogs/create-payment-request-dialog/create-payment-request-dialog.component";
import { AuthService } from "../../../services/auth.service";
import { AddDiscountCodeFormComponent } from "../../../dialogs/add-discount-code-form/add-discount-code-form.component";
import { ResultHandler } from "../../../common/result-handler";
import { PaymentComponent } from "../../../dialogs/cash-register/payment/payment.component";
import { interval, of, Subscription } from "rxjs";
import { catchError, first, switchMap } from "rxjs/operators";

@Component({
    selector: 'app-order-detail-actions',
    templateUrl: './order-detail-actions.component.html',
    standalone: false
})
export class OrderDetailActionsComponent implements OnInit {
  static numberOfButtons = 0;
  static buttons = {
    Close: OrderDetailActionsComponent.numberOfButtons++,
    Edit: OrderDetailActionsComponent.numberOfButtons++,
    SendPaymentRequest: OrderDetailActionsComponent.numberOfButtons++,
    DownloadETickets: OrderDetailActionsComponent.numberOfButtons++,
    PrintOrderConfirmation: OrderDetailActionsComponent.numberOfButtons++,
    PrintInvoice: OrderDetailActionsComponent.numberOfButtons++,
    PrintTickets: OrderDetailActionsComponent.numberOfButtons++,
    PayByInvoice: OrderDetailActionsComponent.numberOfButtons++,
    Renew: OrderDetailActionsComponent.numberOfButtons++,
  };
  // Buttons whose visibility is computed from order state (e.g. only show "Renew" when the order has PP lines).
  // Excluded from the default "show everything" init so they remain hidden until their condition is met.
  static conditionalButtons: ReadonlySet<string> = new Set(['Renew']);
  
  @ViewChild('paymentOverview') paymentOverview;
  public isIbaActive: boolean = false;
  public showButton: boolean[] = [];
  public isInArchiveMode = this.authService.isInArchiveMode();
  public isInEditMode: boolean = false;
  public isReadOnly: boolean = false;
  public isLroRunning: boolean = false;

  @Output() editModeOutput = new EventEmitter<boolean>();
  @Output() orderIsPaid = new EventEmitter<boolean>();
  @Output() renew = new EventEmitter<void>();

  private _orderDetails: OrderDetailsView;
  @Input() set orderDetails(value: OrderDetailsView) {
    this._orderDetails = value;
    if (value) {
      this.checkForPendingLro(value.Id);
      this.showButton[OrderDetailActionsComponent.buttons.Renew] =
        !!value.OrderLines?.some(ol => ol.CustomerPassePartoutId);
    }
  }
  get orderDetails(): OrderDetailsView { return this._orderDetails; }

  constructor(
    private apploader: HttpLoaderService,
    private cultureService: CultureService,
    private translate: TranslateService,
    private applicationModuleHelperService: ApplicationModuleHelperService,
    private confirmService: ConfirmService,
    private snackBar: SnackbarService,
    private orderService: OrderService,
    private orderActionService: OrderActionService,
    private orderDetailsService: OrderDetailsService,
    private invoiceBatchService: InvoiceBatchService,
    private dialog: MatDialog,
    private resultHandler: ResultHandler,
    private ticketService: TicketService,
    private mailTemplateService: MailTemplateService,
    private authService: AuthService,
    private notificationCommunicationService: NotificationCommunicationService,
    private lroService: LongRunningOperationService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.applicationModuleHelperService.getActiveModules().subscribe((activeModules) => {
      if (activeModules !== null) {
        this.isIbaActive = activeModules.some((module) => module.toUpperCase() == 'IBA')
      }
    });

    for (const key in OrderDetailActionsComponent.buttons) {
      if (OrderDetailActionsComponent.conditionalButtons.has(key)) {
        continue;
      }
      
      this.showButton[OrderDetailActionsComponent.buttons[key]] = true;
    }
  }

  payOrder() {
    const title = this.translate.instant('SALES.CASH-REGISTER.ORDER-OVERVIEW.DIALOG.PAY-ORDERS.TITLE');
    const dialogRef: MatDialogRef<any> = this.dialog.open(PaymentComponent, {
      width: '1024px',
      disableClose: true,
      data: { title: title, payload: [this.orderDetails.Id], bookingCustomerId: this.orderDetails.CustomerId }
    });

    dialogRef.afterClosed().subscribe(submitForm => {
      if (submitForm) {
        this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.PAYMENT-HANDLED'), 'GENERIC.SNACKBAR.CLOSE');
        this.orderIsPaid.emit(true);
      }
    });
  }

  initiatePayPerEmail() {

    const amountToRequest = (this.orderDetails.Balance - this.orderDetails.PendingPaymentBalance);
    const displayAmount = this.cultureService.formatCurrency(amountToRequest);

    const dialogData: CreatePaymentRequestDialogData = {
      title: this.translate.instant('GENERIC.CONFIRM.WARNING.TITLE.ARE-YOU-SURE'),
      message: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.SEND-PAY-PER-MAIL.MSG', { amount: displayAmount }),
      email: this.orderDetails.PrimaryContactEmail
    };

    const dialogRef: MatDialogRef<CreatePaymentRequestDialogComponent> = this.dialog.open(CreatePaymentRequestDialogComponent, {
      width: '380px',
      disableClose: true,
      panelClass: 'confirmBox',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: CreatePaymentRequestDialogResult) => {
      if (!result?.confirmed) {
        return;
      }

      this.apploader.open();
      const args: SendPaymentInvitationArgs = {
        Email: result.email
      }
      this.orderService.sendPaymentRequest(this.orderDetails.Id, args).subscribe((ok: boolean) => {
        this.apploader.close();
        if (ok === true) {
          this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.SEND-PAY-PER-MAIL.SUCCESS'), 'GENERIC.SNACKBAR.CLOSE');
          this.paymentOverview.loadPaymentHistoryForOrder(this.orderDetails.Id);
        } else {
          this.confirmService.confirm({
            title: this.translate.instant('SALES.ORDERS.DETAILS.CONFIRM.SEND-PAY-PER-MAIL.FAILED.TITLE'),
            message: this.translate.instant('SALES.ORDERS.DETAILS.CONFIRM.SEND-PAY-PER-MAIL.FAILED.MSG'),
            okonly: true
          }).subscribe();
        }
      });
    });
  }

  getButtons() {
    return OrderDetailActionsComponent.buttons;
  }

  printPackagingSlip() {
    this.apploader.open();

    this.orderService.getPackagingSlip(this.orderDetails.Id).subscribe((pdf: Blob) => {
      FileSaver.saveAs(pdf, `order_confirmation_${this.orderDetails.Id}.pdf`);
      this.apploader.close();
    });
  }

  printInvoice() {
    this.apploader.open();

    this.orderService.getInvoice(this.orderDetails.Id).subscribe((pdf: Blob) => {
      FileSaver.saveAs(pdf, `invoice_${this.orderDetails.Id}.pdf`);
      this.apploader.close();
    });
  }

  createInvoice() {
    const orderId = this.orderDetails.Id;

    this.confirmService.confirm({
      title: this.translate.instant('SALES.ORDERS.DETAILS.CONFIRM.DIRECT-INVOICE.TITLE'),
      message: this.translate.instant('SALES.ORDERS.DETAILS.CONFIRM.DIRECT-INVOICE.MSG')
    }).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.apploader.open();
      const args: CreateInvoiceBatchByOrderIdsArgs = {
        OrderIds: [orderId]
      }
      this.invoiceBatchService.createInvoiceBatchByOrderIds(args).subscribe((invoicebatchId: number) => {
        // Batch direct factureren.
        this.invoiceBatchService.invoicing(invoicebatchId).subscribe(() => {
          this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.DIRECT-INVOICE-CREATED'));
          this.orderDetails.HasPayments = true;
          this.orderIsPaid.emit(true);
          this.apploader.close();
        });
      });
    });
  }

  createCreditOrDebitInvoice() {
    const orderId = this.orderDetails.Id;
    const invoiceType = this.orderDetails.Balance < 0 ? InvoiceTypeEnum.Credit : InvoiceTypeEnum.Debet;
    const messageKey = this.orderDetails.Balance < 0
      ? 'SALES.ORDERS.DETAILS.CONFIRM.CREDIT-INVOICE'
      : 'SALES.ORDERS.DETAILS.CONFIRM.DEBIT-INVOICE';

    this.confirmService.confirm({
      title: this.translate.instant(messageKey + '.TITLE'),
      message: this.translate.instant(messageKey + '.MSG')
    }).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.apploader.open();
      const args: CreateInvoiceBatchByOrderIdsArgs = {
        OrderIds: [orderId],
        InvoiceType: invoiceType
      };
      this.invoiceBatchService.createInvoiceBatchByOrderIds(args).subscribe((invoicebatchId: number) => {
        this.invoiceBatchService.invoicing(invoicebatchId).subscribe(() => {
          this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.INVOICE-CREATED'));
          this.apploader.close();

          // Emit event to refresh order details and payment overview
          // This ensures the payment overview is updated with the new payment from the credit invoice
          this.orderIsPaid.emit(true);
        });
      });
    });
  }

  displayHardcardDialog() {
    const title = this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.PRINT-HARDCARDS.TITLE');
    const dialogRef: MatDialogRef<any> = this.dialog.open(PrintOrderHardcardsComponent, {
      width: '1024px',
      disableClose: true,
      data: { title: title, payload: this.orderDetails }
    });
    dialogRef.afterClosed().subscribe();

    return false;
  }

  displayPrintDialog() {
    this.apploader.open();

    this.orderDetailsService.getPrintDetails(this.orderDetails.Id, false).subscribe(data => {

      this.apploader.close();

      const dialogOptions = DialogHelper.GetDialogOptions(DialogWidth.xlg, {
        title: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.PRINT-TICKETS.TITLE'),
        payload: data, mode: 'print', isIbaActive: this.isIbaActive
      });

      const dialogRef: MatDialogRef<any> = this.dialog.open(PrintOrDownloadTicketsComponent, dialogOptions);

      dialogRef.afterClosed()
        .subscribe(submitForm => {
          if (submitForm === null) {
            // Nothing to do. The download is closed without pressing download/print.
            return;
          }

          this.apploader.open();

          this.route.params.subscribe((params: Params) => {
            const isSecondaryOrder = params.secondaryOrder !== undefined;
            
            const args: TicketPrintArgs = {
              OrderId: isSecondaryOrder ? null : this.orderDetails.Id,
              SecondaryOrderId: isSecondaryOrder ? this.orderDetails.Id : null,
              SelectedEventPlacementIds: submitForm.SelectedEventplacementIds,
              PackagingSlipOption: submitForm.packagingSlip,
              CutMode: submitForm.cutMode,
              isDuplicate: false,
              DuplicationReasonId: submitForm.DuplicationReasonId,
            }

            //to the download
            this.ticketService.printTicket(args).subscribe(data => {
              this.apploader.close();
              this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.PRINTJOB-CREATED'), 'GENERIC.SNACKBAR.CLOSE');
            }, error => {

              this.apploader.close();

              this.confirmService.confirm({
                title: this.translate.instant('GENERIC.SNACKBAR.SOMETHING-WENT-WRONG'),
                message: this.translate.instant('SALES.ORDERS.DETAILS.CONFIRM.PRINT-TICKETS.FAILED.MSG'),
                okonly: true
              }).subscribe();
            });
          });
        }
        );
    });

    return false;
  }

  displayDownloadDialog() {
    this.apploader.open();
    setTimeout(() => {
      this.orderDetailsService.getPrintDetails(this.orderDetails.Id, true).subscribe(data => {

        this.apploader.close();

        const dialogOptions = DialogHelper.GetDialogOptions(DialogWidth.xlg, {
          title: this.translate.instant('SALES.ORDERS.DETAILS.CONFIRM.DOWNLOAD-ETICKETS.TITLE'),
          payload: data, mode: 'download', allowSalesChannelSelect: true, isIbaActive: this.isIbaActive
        });

        const dialogRef: MatDialogRef<any> = this.dialog.open(PrintOrDownloadTicketsComponent, dialogOptions);

        dialogRef.afterClosed()
          .subscribe(submitForm => {

            if (submitForm === null) {
              // Nothing to do. The download is closed without pressing download
              return;
            }

            this.apploader.open();

            const args: TicketDownloadArgs = {
              OrderId: this.orderDetails.Id,
              SelectedEventPlacementIds: submitForm.SelectedEventplacementIds,
              SalesChannelId: submitForm.SalesChannelId,
              isDuplicate: false,
              DuplicationReasonId: submitForm.duplicationReasonId
            }

            if (submitForm.DownloadInBackground) {
              this.lroService.registerTicketDownload(args)
                .pipe(first())
                .subscribe(status => {
                  this.notificationCommunicationService.updateNotificationsAndLROS();
                  this.apploader.close();
                  this.snackBar.open('GENERIC.SNACKBAR.BACKGROUND_JOB_CREATED', 'GENERIC.SNACKBAR.CLOSE');
                });
            }
            else {
              //to the download
              this.ticketService.downloadTicket(args).subscribe((data: Blob) => {
                this.apploader.close();
                FileSaver.saveAs(data, `E-tickets_order_${this.orderDetails.Id}.zip`);
              });
            }
          });
      });
    });

    return false;
  }

  displayEmailDialog() {
    let data = <ManualMailDialogParameters>{};
    data.title = 'ORDER.DETAILS.TICKETS.SEND-TICKETS.TITLE';
    data.email = this.orderDetails.CustomerEmail;
    data.showSalesChannelSelection = true;
    data.showAttachments = true;

    this.apploader.open();

    this.orderDetailsService.getPrintDetails(this.orderDetails.Id, true).subscribe(printDetails => {
      data.attachments = printDetails.PrintableTickets;
      data.salesChannels = printDetails.ETicketPrintableChannels;

      this.mailTemplateService.getConfirmationMail(this.orderDetails.Id).subscribe((result) => {
        this.apploader.close();
        data.body = result.Body;
        data.subject = result.Subject;

        const dialogRef: MatDialogRef<any> = this.dialog.open(ManualMailDialogComponent, {
          width: '720px',
          disableClose: true,
          data: data
        });

        dialogRef.afterClosed()
          .subscribe((result: ManualMailDialogResult) => {
            if (!result) {
              return;
            }

            this.apploader.open();

            const args: SendMailETicketArgs = {
              Email: result.email,
              Subject: result.subject,
              Body: result.body,
              SalesChannelId: result.salesChannelId,
              EventPlacementIds: result.attachments
            }

            this.orderService.sendETicketMail(this.orderDetails.Id, args).subscribe((a) => {
              this.apploader.close();
              this.snackBar.open(this.translate.instant('GENERIC.SNACKBAR.EMAIL-HAS-BEEN-SENT'), 'GENERIC.SNACKBAR.CLOSE');
            });
          });
      });
    });

    return false;
  }

  toggleEditMode() {
    this.isInEditMode = !this.isInEditMode;
    this.editModeOutput.emit(this.isInEditMode);
  }

  getInvoiceButtonText() {
    return this.orderDetails.Balance < 0
      ? "ORDER.DETAILS.BUTTONS.CREATE-CREDIT-INVOICE"
      : "ORDER.DETAILS.BUTTONS.CREATE-DEBIT-INVOICE";
  }

  addDiscountCode() {
    const title = this.translate.instant('SALES-ENTITY-MODIFICATION.DIALOG.TITLE.INPUT-DISCOUNT');

    const options = DialogHelper.GetDialogOptions(DialogWidth.lg, { title: title });
    const dialogRef: MatDialogRef<any> = this.dialog.open(AddDiscountCodeFormComponent, options);

    dialogRef.afterClosed().subscribe(res => {
      this.apploader.open();
      if (!res) {
        this.apploader.close();
        return;
      }
      this.orderActionService.applyDiscountCodes(this.orderDetails.Id, JSON.stringify(res.value)).subscribe({

        next: (result: any) => {

          if (result.Value.Problems.length == 0) {
            this.orderIsPaid.emit(true);
          }
          else {
            this.resultHandler.handle(result.Value);
          }

          this.apploader.close();
        }
      });
    });
  }

  openBulkCreditDialog() {
    const creditableLines = this.orderDetails.OrderLines?.filter(ol => !ol.IsCredited) || [];

    if (creditableLines.length === 0) {
      this.confirmService.confirm({
        title: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.BULK-CREDIT.TITLE'),
        message: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.BULK-CREDIT.NO-LINES'),
        okonly: true
      }).subscribe();
      return;
    }

    const dialogRef: MatDialogRef<any> = this.dialog.open(BulkCreditOrderLinesComponent, {
      width: '1100px',
      disableClose: true,
      data: {
        title: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.BULK-CREDIT.TITLE'),
        orderLines: this.orderDetails.OrderLines,
        orderId: this.orderDetails.Id
      }
    });

    dialogRef.afterClosed().subscribe((lro: LongRunningOperationModel) => {
      if (!lro) {
        return;
      }

      this.isInEditMode = false;
      this.editModeOutput.emit(false);
      this.isLroRunning = true;

      this.notificationCommunicationService.updateNotificationsAndLROS();
      this.snackBar.open('GENERIC.SNACKBAR.BACKGROUND_JOB_CREATED', 'GENERIC.SNACKBAR.CLOSE');
      this.pollLroUntilComplete(lro.Id);
    });
  }

  private checkForPendingLro(orderId: number): void {
    this.lroService.getPendingBulkCreditForOrder(orderId).pipe(first()).subscribe(lro => {
      if (lro && lro.IsPending) {
        this.isLroRunning = true;
        this.pollLroUntilComplete(lro.Id);
      } else {
        this.isLroRunning = false;
      }
    });
  }

  private pollLroUntilComplete(lroId: number): void {
    const polling: Subscription = interval(2500).pipe(
      switchMap(() => this.lroService.getLongRunningOperation(lroId).pipe(
        catchError(() => of(null))
      ))
    ).subscribe(lro => {
      if (lro && !lro.IsPending) {
        polling.unsubscribe();
        this.isLroRunning = false;
        this.orderIsPaid.emit(true);
      }
    });
  }
}
